import { useCallback, useState, useEffect } from 'react';
import { UploaderFile } from '../../../common/Uploader';

type Result = {
  selectedFileIndex: number;
  selectedFile?: UploaderFile;
  selectFile: (fileId: string) => void;
  goToNextFile: () => void;
  goToPrevFile: () => void;
};

export function useDocumentsNavigation(files: UploaderFile[]): Result {
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);
  const [highestServerId, setHighestServerId] = useState(0);

  // handles selected index when something gets deleted and index is out of bounds
  useEffect(() => {
    const isLastFileSelectedAndGotRemoved = selectedFileIndex >= files.length && files.length > 0;
    if (isLastFileSelectedAndGotRemoved) {
      setSelectedFileIndex(Math.max(0, files.length - 1));
    }

    if (files.length === 0) {
      setSelectedFileIndex(0);
    }
  }, [files, selectedFileIndex]);

  // Select newest uploaded file
  // When uploading a file, ids form the server are incremental
  // So we sort files by server-assigned ID in descending order and select the first one
  // biome-ignore lint/correctness/useExhaustiveDependencies: unknown
  useEffect(() => {
    if (files.length > 0) {
      const sortedFiles = [...files].sort((a, b) => {
        if (!a.response?.body.id || !b.response?.body.id) return 0;

        return b.response.body.id - a.response.body.id;
      });

      const newestFile = sortedFiles[0];
      const serverId = newestFile?.response?.body.id;

      if (!serverId) {
        return;
      }

      // a file was deleted, whatever we found in the local files is not the newest upload
      if (highestServerId >= serverId) {
        return;
      }

      const index = files.findIndex((file) => file.id === newestFile.id);
      if (index === -1) {
        return;
      }

      setHighestServerId(serverId);
      setSelectedFileIndex(index);
    }
  }, [files]);

  const selectFile = useCallback(
    (fileId: string) => {
      const index = files.findIndex((file) => file.id === fileId);
      setSelectedFileIndex(index !== -1 ? index : 0);
    },
    [files]
  );

  const goToNextFile = useCallback(() => {
    setSelectedFileIndex((index) => (index + 1) % files.length);
  }, [files]);

  const goToPrevFile = useCallback(() => {
    setSelectedFileIndex((index) => (index - 1 + files.length) % files.length);
  }, [files]);

  return {
    selectedFileIndex,
    selectedFile: files[selectedFileIndex],
    selectFile,
    goToNextFile,
    goToPrevFile,
  };
}
